import React, { useState, useEffect } from 'react';
import { askGPT } from '../components/OpenAIService';

const ObjectifUpdater = ({
  initialObjective,
  conversationHistory,
  onObjectiveUpdate,
  objectiveType
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState('');
  
  // Observer pour détecter les nouveaux messages
  useEffect(() => {
    if (!conversationHistory || conversationHistory.length === 0) return;
    
    const lastMessage = conversationHistory[conversationHistory.length - 1];
    
    // Si le dernier message est de l'utilisateur, on analyse s'il faut mettre à jour l'objectif
    if (lastMessage.type === 'user') {
      setLastUserMessage(lastMessage.text);
      
      // Vérifier si c'est une question ou une demande d'information
      if (!isUserAskingQuestion(lastMessage.text)) {
        updateObjectiveBasedOnUserInput(lastMessage.text);
      }
    }
  }, [conversationHistory]);
  
  // Fonction pour détecter si l'utilisateur pose une question
  const isUserAskingQuestion = (text) => {
    // Recherche de points d'interrogation
    if (text.includes('?')) return true;
    
    // Recherche de mots interrogatifs en début de phrase
    const interrogativeWords = [
      'comment', 'pourquoi', 'quand', 'où', 'qui', 'que', 'quoi', 'quel', 'quelle', 
      'quels', 'quelles', 'est-ce', 'puis-je', 'peux-tu', 'pouvez-vous', 'peut-on'
    ];
    
    const lowerText = text.toLowerCase();
    return interrogativeWords.some(word => 
      lowerText.startsWith(word) || 
      lowerText.startsWith(`${word} `) || 
      lowerText.includes(` ${word} `)
    );
  };
  
  // Fonction pour mettre à jour l'objectif en fonction des réponses de l'utilisateur
  const updateObjectiveBasedOnUserInput = async (userMessage) => {
    setIsUpdating(true);
    
    try {
      // Construction du prompt pour GPT
      const objectiveTypeInFrench = 
        objectiveType === 'project' ? 'projet' : 
        objectiveType === 'idea' ? 'idée' : 'désir/aspiration';
      
      // Construire l'historique des messages pour le contexte
      let contextMessages = [];
      for (let i = conversationHistory.length - 1; i >= 0; i--) {
        contextMessages.unshift(conversationHistory[i]);
        if (contextMessages.length >= 6) break; // Limiter à 3 échanges pour le contexte
      }
      
      const context = contextMessages.map(m => 
        `${m.type === 'bot' ? 'Assistant' : 'Utilisateur'}: ${m.text}`
      ).join('\n\n');
      
      // Prompt pour demander à GPT de reformuler l'objectif
      const prompt = `
Tu es un expert en formulation d'objectifs ${objectiveTypeInFrench}.

Contexte de la conversation récente:
${context}

L'objectif actuel est : "${initialObjective}"

En te basant sur les derniers échanges, détermine si l'objectif doit être reformulé ou amélioré.

IMPORTANT:
1. Si l'utilisateur pose une question ou demande des informations, ne modifie PAS l'objectif et réponds "NO_UPDATE".
2. Si l'utilisateur n'a pas fourni de nouvelles informations pertinentes pour l'objectif, réponds "NO_UPDATE".
3. Sinon, reformule l'objectif pour le rendre plus précis, clair et actionnable en intégrant UNIQUEMENT les nouvelles informations pertinentes.

Réponds UNIQUEMENT avec le nouvel objectif reformulé OU "NO_UPDATE", sans explications ni préambule.
      `;
      
      // Appel à l'API GPT
      const response = await askGPT(prompt);
      
      // Vérifier si une mise à jour est nécessaire
      if (response && response.trim() !== "NO_UPDATE" && response !== initialObjective) {
        onObjectiveUpdate(response);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'objectif:', error);
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Ce composant n'a pas de rendu visible
  return null;
};

export default ObjectifUpdater;